import { useFormik } from "formik";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Spinner from "../../components/misc/Spinner";
import { useToastStore } from "../../stores/toast";
import { useUserSettingsStore } from "../../stores/userSettings";

const DEFAULT_SETTINGS = {
  email: { delivery_note_delivered: false },
};

const validationSchema = Yup.object({
  email: Yup.object({
    delivery_note_delivered: Yup.boolean(),
  }),
});

function NotificationsTab({ userId }) {
  const { t } = useTranslation();
  const addToast = useToastStore((state) => state.addToast);
  const getNotifications = useUserSettingsStore((s) => s.getNotifications);
  const updateNotifications = useUserSettingsStore((s) => s.updateNotifications);

  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [saving, setSaving] = useState(false);

  const formik = useFormik({
    initialValues: DEFAULT_SETTINGS,
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values) => {
      setSaving(true);
      const { error } = await updateNotifications({
        userId,
        payload: { settings: values },
      });
      setSaving(false);
      if (error) {
        const msg = error?.detail?.[0]?.t || error?.detail;
        addToast("error", msg ? t(msg) : t("errors.unknown"));
        return;
      }
      formik.resetForm({ values });
      addToast("success", t("common.saved"));
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getNotifications({ userId }).then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) {
        addToast("error", t("errors.unknown"));
        setLoading(false);
        return;
      }
      setEligible(Boolean(data.eligible));
      formik.resetForm({ values: data.settings || DEFAULT_SETTINGS });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return <Spinner />;
  }

  if (!eligible) {
    return (
      <div className="text-dark text-opacity-50">
        {t("notifications.tab.ineligible_placeholder")}
      </div>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="form-check form-switch mb-3">
        <input
          type="checkbox"
          className="form-check-input"
          id="email.delivery_note_delivered"
          name="email.delivery_note_delivered"
          checked={formik.values.email.delivery_note_delivered}
          onChange={(e) =>
            formik.setFieldValue("email.delivery_note_delivered", e.target.checked)
          }
        />
        <label className="form-check-label" htmlFor="email.delivery_note_delivered">
          {t("notifications.fields.email.delivery_note_delivered")}
        </label>
      </div>

      <button
        type="submit"
        className="btn btn-success"
        disabled={saving || !formik.dirty}
      >
        {saving && <i className="fa fa-spinner fa-spin me-1" />}
        {t("common.save")}
      </button>
    </form>
  );
}

NotificationsTab.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default NotificationsTab;
